import React, { useEffect, useMemo, useRef, useState } from 'react';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import { AdminNav, HeaderNavigation, MobileHeaderNav, MockUserNav } from '@zg-rentals/rental-manager-header-navigation';

import useGlobalKeyDown from '../hooks/useGlobalKeyDown';

import { getListingAliasFromPath, shouldBlockPath } from '../utils/';

import { HorizontalLayoutVerticalNavigationContainer } from '../HorizontalLayoutVerticalNavigationContainer';

import { SpinnerContainer } from '../SpinnerContainer';

import {
  StyledAdminContainer,
  StyledAdminModalBody,
  StyledAdminModalBodyHeading,
  StyledAdminModalInput,
  StyledAdminModalInputContainer,
  StyledAppLayoutContainer,
  StyledBodyContainer,
  StyledBodyContentContainer,
  StyledCloseBtnContainer,
  StyledDisplayUntilMinWidthXL,
  StyledHideUntilMinWidthXLLarge,
  StyledLayoutContainer,
  StyledPageContent,
  StyledSpinnerContainerPage,
  StyledWarningText,
} from './styles';

import type { HeaderNavigationProps, User } from '@zg-rentals/rental-manager-header-navigation';
import {
  AdornedInput,
  Adornment,
  Button,
  ButtonGroup,
  IconCloseCircle,
  ModalDialog,
  Text,
  TextButton,
  Trigger,
} from '@zillow/constellation';

const DEFAULT_DENY_LIST = [
  // will match any string that starts with /rental-manager and optionally ends with a trailing slash
  /^\/rental-manager(?:\/$)?$/,
  // will match strings that start with "/rental-manager/post-a-listing" and optionally end with a trailing slash
  /^\/rental-manager\/post-a-listing(?:\/$)?$/,
  // will matches any string that starts with "rental-manager/market-trends", optionally followed by a slash, and then any characters
  /\/rental-manager\/market-trends(?:\/$)?.*/,
  // will matches any string that starts with "rental-manager/price-my-rental", optionally followed by a slash, and then any characters
  /\/rental-manager\/price-my-rental(?:\/$)?.*/,
  // will matches any string that starts with "rental-manager/login", optionally followed by a slash, and then any characters
  /\/rental-manager\/login(?:\/$)?.*/,
  // will match any string that starts with "/rental-manager/leasing/colandlord" and optionally ends with a trailing slash, and then any characters
  /^\/rental-manager\/leasing\/colandlord(?:\/$)?.*$/,
  // will match any string that starts with "/rental-manager/property-condition/colandlord" and optionally ends with a trailing slash, and then any characters
  /^\/rental-manager\/property-condition\/colandlord(?:\/$)?.*$/,
  // will match any string that starts with "/renter-hub/property-condition" and optionally ends with a trailing slash, and then any characters
  /^\/renter-hub\/property-condition(?:\/$)?.*$/,
  // will match strings that start with "/rental-manager/tenant-placement" and optionally end with a trailing slash, and then any characters
  /^\/rental-manager\/tenant-placement(?:\/$)?.*$/,
];

const isWindowDefined = typeof window !== 'undefined';

interface Props extends HeaderNavigationProps {
  /**
   * 
   * a list of paths that will be denied from the horizontal layout
    ex:  
    const denyList = [
      "/admin", // Exact path
      /\/api\/.*\/, // Pattern: Any path starting with /api/
      /\/user\/(edit|delete)/, // Pattern: Paths ending with /edit or /delete under /user/
    ];
   */
  horizontalLayoutDenyList?: Array<string | RegExp>;
  // set the loading state of the container
  isLoading?: boolean;
  // sets the value to hide the header navigation
  hideHeaderNavigation?: boolean;
  // set the value to display the horizontal layout
  isLayoutHorizontal?: boolean;
  /*
   * pathname
   *
   * the current path so the link can be highlighted
   */
  pathname: string;
  renderLoading?: () => JSX.Element;
  /**
   * body content
   * common use cases can be JSX or a Switch component from react-router-dom
   *
   * <Switch>
   *   <Route path="path" component={component} />
   * </Switch>
   */
  bodyContent?: React.ReactNode;
  // can use children to render the body content.  this will take persistence over bodyContent
  children?: React.ReactNode;
  userModel: User;
  isInMobileWebView?: boolean;
}

const DEFAULT_ADMIN_BANNER_TIMEOUT = 5;

const getIsLayoutHorizontal = (shouldBlockViaDenyList: boolean, isLayoutHorizontal: boolean) => {
  return !shouldBlockViaDenyList && isLayoutHorizontal;
};

const Loading = (props: Record<string, unknown>) => (
  <StyledSpinnerContainerPage data-testid="LoadingContainer" backgroundColor="white" {...props}>
    <StyledPageContent margin={0} padding={0} fluid={true}>
      <SpinnerContainer size={'lg'} height={'100%'} />
    </StyledPageContent>
  </StyledSpinnerContainerPage>
);

const AppLayoutContainer = ({
  /**
   * altLogo true - zrm logo
   * altLogo false - zillow logo
   */
  altLogo = true,
  hideHeaderNavigation = false,
  horizontalLayoutDenyList = DEFAULT_DENY_LIST,
  isLayoutHorizontal = true,
  isLoading = false,
  pathname,
  bodyContent: aBodyContent = null,
  children = null,
  userModel = {} as User,
  isInMobileWebView = false,
  ...rest
}: Props) => {
  const [isMobileMenuExpanded, setIsMobileMenuExpanded] = useState(false);

  const [displayAdminContainer, setDisplayAdminContainer] = useState<boolean>(true);
  const [adminTimeoutRemaining, setAdminTimeoutRemaining] = useState<number | string>(DEFAULT_ADMIN_BANNER_TIMEOUT);
  const [inputAdminTimeoutRemaining, setInputAdminTimeoutRemaining] = useState<number | string>(
    DEFAULT_ADMIN_BANNER_TIMEOUT,
  );

  const adminBannerTimerIdRef = useRef<number | null | false>(null);

  // these variables and functions are at the top because they need to be declared before they are used and we need them immediately
  const blockPath = shouldBlockPath({
    denyList: horizontalLayoutDenyList,
    path: pathname,
  });

  const isHorizontal = getIsLayoutHorizontal(blockPath, isLayoutHorizontal);

  // react hooks
  const { impersonatorEmail, impersonatorUserToken, isMocked, isPostingPathAdmin } = userModel;
  const { brand, useRelativePaths, reactRouterLink, host } = rest;

  const useAbsolutePaths = !useRelativePaths || !reactRouterLink;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, host).getAllPaths();
  const { signInPage: signInPageFunc } = paths;
  const isImpersonator = Boolean(impersonatorEmail && impersonatorUserToken && isPostingPathAdmin);

  const listingAlias = getListingAliasFromPath(pathname);

  const signInPage = signInPageFunc(pathname) || '';
  const bodyContent = React.Children.count(children) > 0 ? children : aBodyContent;

  // style props
  const fullViewportHeight = isHorizontal ? true : false;
  const offsetHeight = isHorizontal ? 50 : 0;
  const setBackgroundColor = isHorizontal ? true : false;

  /**
   * check for key combination press: command + ctrl + a
   * this will toggle the admin container display
   */
  useGlobalKeyDown({
    callback: () => {
      if (displayAdminContainer) {
        hideAdminContainer();
      } else {
        showAdminContainer();
      }
    },
  });

  useEffect(() => {
    return () => clearAdminContainerInterval();
  }, []);

  useEffect(() => {
    if (typeof adminTimeoutRemaining === 'number' && adminTimeoutRemaining <= 0) {
      showAdminContainer();
    }
  }, [adminTimeoutRemaining]);

  const handleStopImpersonatingClick = () => {
    // fill in here
  };

  const conditionalNavigationContainer = useMemo(
    () => (
      <>
        {isHorizontal ? (
          <>
            {hideHeaderNavigation === false && (
              <HorizontalLayoutVerticalNavigationContainer
                altLogo={altLogo}
                hideHeaderNavigation={hideHeaderNavigation}
                pathname={pathname}
                userModel={userModel}
                {...rest}
              />
            )}
          </>
        ) : (
          <>
            {hideHeaderNavigation === false && (
              <HeaderNavigation
                altLogo={altLogo}
                listingId={listingAlias}
                pathname={pathname}
                userModel={userModel}
                {...rest}
              />
            )}
          </>
        )}
      </>
    ),
    [hideHeaderNavigation, isHorizontal, listingAlias, pathname, rest, userModel],
  );

  const layout = useMemo(() => {
    return (
      <StyledLayoutContainer
        data-testid={isHorizontal ? 'HorizontalLayoutContainer' : 'VerticalLayoutContainer'}
        isHorizontal={isHorizontal}
        isMobileMenuExpanded={isMobileMenuExpanded}
      >
        {!isInMobileWebView && !hideHeaderNavigation ? (
          <StyledDisplayUntilMinWidthXL>
            <MobileHeaderNav
              altLogo={altLogo}
              isMobileMenuExpanded={isMobileMenuExpanded}
              handleToggle={setIsMobileMenuExpanded}
              pathname={pathname}
              userModel={userModel}
              {...rest}
            />
          </StyledDisplayUntilMinWidthXL>
        ) : null}

        <StyledHideUntilMinWidthXLLarge>{conditionalNavigationContainer}</StyledHideUntilMinWidthXLLarge>

        <StyledBodyContainer
          data-testid="BodyContainer"
          hideHeaderNavigation={hideHeaderNavigation}
          isHorizontal={isHorizontal}
        >
          <StyledBodyContentContainer data-testid="BodyContentContainer">{bodyContent}</StyledBodyContentContainer>
        </StyledBodyContainer>
      </StyledLayoutContainer>
    );
  }, [
    bodyContent,
    conditionalNavigationContainer,
    hideHeaderNavigation,
    isHorizontal,
    listingAlias,
    pathname,
    rest,
    userModel,
  ]);

  const footer = (close: React.FC) => (
    <ButtonGroup aria-label="Modal actions">
      <TextButton onClick={close}>Cancel</TextButton>
      <Button
        buttonType="primary"
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          close(event);
          handleAdminModalConfirmClick();
        }}
      >
        Confirm
      </Button>
    </ButtonGroup>
  );

  const adminModal = (props: object, close: React.FC) => (
    <ModalDialog
      {...props}
      size="md"
      renderHeader={<Text>Admin Banner</Text>}
      renderBody={
        <StyledAdminModalBody>
          <StyledAdminModalBodyHeading as="div" fontType="body" marginBottom="sm">
            Banner will re appear in:
          </StyledAdminModalBodyHeading>

          <StyledAdminModalInputContainer>
            <AdornedInput
              input={
                <StyledAdminModalInput
                  type="text"
                  placeholder={DEFAULT_ADMIN_BANNER_TIMEOUT?.toString()}
                  value={inputAdminTimeoutRemaining}
                  {...({ fontType: 'body' } as any)}
                  maxLength={3}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = event.target.value;

                    if (Number(newValue) > 0) {
                      setInputAdminTimeoutRemaining(Number(newValue));
                    }

                    if (newValue === '') {
                      setInputAdminTimeoutRemaining(newValue);
                    }
                  }}
                />
              }
              rightAdornment={<Adornment htmlFor="AdornedInput-manual">s</Adornment>}
            />
          </StyledAdminModalInputContainer>

          {isImpersonator && (
            <StyledWarningText as="div" fontType="bodySmall" fontColor="red400" marginBottom="md">
              You will still be impersonating a user.
            </StyledWarningText>
          )}

          <Text
            as="div"
            fontType="body"
            fontColor="coolGray500"
          >{`Press \u2318 + Ctrl + A to toggle the admin banner.`}</Text>
        </StyledAdminModalBody>
      }
      renderFooter={footer(close)}
    />
  );

  const clearAdminContainerInterval = () => {
    if (adminBannerTimerIdRef.current) {
      isWindowDefined && window.clearInterval(adminBannerTimerIdRef.current);
      adminBannerTimerIdRef.current = null;
    }
  };

  const startAdminContainerTimeoutInterval = () => {
    clearAdminContainerInterval();

    let nextTime =
      typeof inputAdminTimeoutRemaining === 'number' ? inputAdminTimeoutRemaining : DEFAULT_ADMIN_BANNER_TIMEOUT;

    adminBannerTimerIdRef.current =
      isWindowDefined &&
      window.setInterval(() => {
        nextTime = nextTime - 1;

        setAdminTimeoutRemaining(nextTime);

        if (nextTime <= 0) {
          clearAdminContainerInterval();
          return;
        }
      }, 1000);
  };

  const hideAdminContainer = () => {
    startAdminContainerTimeoutInterval();
    setDisplayAdminContainer(false);
  };

  const showAdminContainer = () => {
    setDisplayAdminContainer(true);
  };

  const handleAdminModalConfirmClick = () => {
    setAdminTimeoutRemaining(inputAdminTimeoutRemaining);

    setTimeout(() => {
      hideAdminContainer();
    }, 10);
  };

  return (
    <StyledAppLayoutContainer
      data-testid="AppLayoutContainer"
      fullViewportHeight={fullViewportHeight}
      offsetHeight={offsetHeight}
      setBackgroundColor={setBackgroundColor}
    >
      {isHorizontal && displayAdminContainer && (
        <StyledAdminContainer>
          {isMocked && <MockUserNav brand="zillow" userModel={userModel} />}

          <AdminNav
            brand={'zillow'}
            paths={paths}
            signInPage={signInPage}
            userModel={userModel}
            handleStopImpersonatingClick={handleStopImpersonatingClick}
            listingId={listingAlias}
          />
          <StyledCloseBtnContainer>
            <Trigger
              triggered={adminModal as () => JSX.Element}
              onOpen={() => {
                setAdminTimeoutRemaining(DEFAULT_ADMIN_BANNER_TIMEOUT);
              }}
            >
              <IconCloseCircle />
            </Trigger>
          </StyledCloseBtnContainer>
        </StyledAdminContainer>
      )}

      {blockPath ? (
        layout
      ) : isLoading ? (
        <Loading data-blockPath={blockPath} data-isLoading={isLoading} data-pathname={pathname} />
      ) : (
        layout
      )}
    </StyledAppLayoutContainer>
  );
};

export default AppLayoutContainer;
